import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Button,
  Paper
} from '@mui/material';
import {
  Science as ScienceIcon,
  LocalHospital as HospitalIcon,
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryIcon,
  Settings as AdminIcon
} from '@mui/icons-material';
import { useOrg } from '../contexts/OrgContext';

const OrgSelect = ({ currentUser }) => {
  const { setOrg } = useOrg();
  const navigate = useNavigate();

  const handleOrgSelect = (orgChoice) => {
    setOrg(orgChoice);
    navigate(`/internal/${orgChoice}/dashboard`);
  };

  // Check if current user is super admin
  const isSuperAdmin = currentUser?.isSuperAdmin || currentUser?.email === 'admin@barebiologics.com';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6, position: 'relative' }}>
        {/* Admin Settings Button (Super Admin Only) - Desktop */}
        {isSuperAdmin && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            right: 0,
            display: { xs: 'none', md: 'block' }
          }}>
            <Button
              variant="outlined"
              startIcon={<AdminIcon />}
              onClick={() => navigate('/admin')}
              sx={{
                borderColor: '#039582',
                color: '#039582',
                '&:hover': {
                  borderColor: '#027A6B',
                  bgcolor: '#f0fdfa'
                }
              }}
            >
              Admin Dashboard
            </Button>
          </Box>
        )}
        
        <Typography variant="h2" component="h1" sx={{ 
          fontWeight: 800, 
          color: '#0f172a', 
          mb: 2,
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Select Organization
        </Typography>
        <Typography variant="h5" sx={{ color: '#64748b', fontWeight: 500, maxWidth: 600, mx: 'auto' }}>
          Choose your organization to access specific operations and management tools
        </Typography>
        
        {/* Admin Settings Button (Super Admin Only) - Mobile */}
        {isSuperAdmin && (
          <Box sx={{ 
            mt: 3,
            display: { xs: 'block', md: 'none' }
          }}>
            <Button
              variant="outlined"
              startIcon={<AdminIcon />}
              onClick={() => navigate('/admin')}
              sx={{
                borderColor: '#039582',
                color: '#039582',
                '&:hover': {
                  borderColor: '#027A6B',
                  bgcolor: '#f0fdfa'
                }
              }}
            >
              Admin Dashboard
            </Button>
          </Box>
        )}
      </Box>

      {/* Organization Selection Cards */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Bare Biologics */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            border: '2px solid #5c725c',
            '&:hover': {
              boxShadow: '0 8px 32px rgba(92, 114, 92, 0.2)',
              transform: 'translateY(-4px)',
              transition: 'all 0.3s ease'
            }
          }}>
            <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <ScienceIcon sx={{ fontSize: 64, color: '#5c725c', mb: 2 }} />
                <Typography variant="h4" component="h2" sx={{ 
                  fontWeight: 700, 
                  color: '#5c725c', 
                  mb: 1 
                }}>
                  Bare Biologics
                </Typography>
                <Typography variant="h6" sx={{ color: '#7a8f7a', fontWeight: 500 }}>
                  Lipoderma Operations
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ 
                color: '#64748b', 
                textAlign: 'center', 
                mb: 3,
                lineHeight: 1.6,
                flexGrow: 1
              }}>
                Specialized in regenerative medicine and tissue engineering solutions. 
                Primary vendor: Britecyte. Purchase orders are automatically pushed to QuickBooks once fully received.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#0f172a', 
                  mb: 2,
                  textAlign: 'center'
                }}>
                  Key Features
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUpIcon sx={{ color: '#5c725c', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        Purchase Orders
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <InventoryIcon sx={{ color: '#5c725c', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        Inventory Management
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ScienceIcon sx={{ color: '#5c725c', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        Quality Control
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUpIcon sx={{ color: '#5c725c', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        QuickBooks Integration
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => handleOrgSelect('bare')}
                sx={{
                  bgcolor: '#5c725c',
                  color: 'white',
                  fontWeight: 600,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: '#4a5d4a',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(92, 114, 92, 0.4)'
                  }
                }}
              >
                Access Bare Biologics
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* EPL */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            border: '2px solid #0d9488',
            '&:hover': {
              boxShadow: '0 8px 32px rgba(13, 148, 136, 0.2)',
              transform: 'translateY(-4px)',
              transition: 'all 0.3s ease'
            }
          }}>
            <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <HospitalIcon sx={{ fontSize: 64, color: '#0d9488', mb: 2 }} />
                <Typography variant="h4" component="h2" sx={{ 
                  fontWeight: 700, 
                  color: '#0d9488', 
                  mb: 1 
                }}>
                  EPL
                </Typography>
                <Typography variant="h6" sx={{ color: '#14b8a6', fontWeight: 500 }}>
                  Equicenta Operations
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ 
                color: '#64748b', 
                textAlign: 'center', 
                mb: 3,
                lineHeight: 1.6,
                flexGrow: 1
              }}>
                Advanced medical device and supply chain management with focus on regulatory compliance, 
                quality assurance, and operational efficiency. Purchase orders are rarely created.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#0f172a', 
                  mb: 2,
                  textAlign: 'center'
                }}>
                  Key Features
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <HospitalIcon sx={{ color: '#0d9488', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        Supply Chain
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <InventoryIcon sx={{ color: '#0d9488', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        Inventory Tracking
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUpIcon sx={{ color: '#0d9488', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        Quality Assurance
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUpIcon sx={{ color: '#0d9488', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        Regulatory Compliance
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => handleOrgSelect('epl')}
                sx={{
                  bgcolor: '#0d9488',
                  color: 'white',
                  fontWeight: 600,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: '#0b7a6e',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(13, 148, 136, 0.4)'
                  }
                }}
              >
                Access EPL
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Information */}
      <Paper sx={{ p: 4, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', mb: 3, textAlign: 'center' }}>
          System Overview
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#5c725c', mb: 1 }}>
                Purchase Orders
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Bare Biologics: Regular orders from Britecyte<br/>
                EPL: Rare creation for major purchases
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d9488', mb: 1 }}>
                Inventory Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Track all materials, supplies, and products<br/>
                Quality control and lot tracking
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f59e0b', mb: 1 }}>
                Integration
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                QuickBooks integration for Bare Biologics<br/>
                Regulatory compliance for EPL
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default OrgSelect;
